import { router } from '@inertiajs/react';

// Fonction utilitaire pour wrapper les soumissions de formulaires existants
export function wrapFormSubmissionWithCsrf<T extends Record<string, any>>(
    originalSubmit: (data: T, options?: any) => void,
    data: T,
    options: any = {}
) {
    // Rafraîchir le token avant soumission si le formulaire est ouvert depuis longtemps
    const refreshTokenIfNeeded = async () => {
        try {
            const response = await window.axios.get('/csrf-token');
            if (response.data.csrf_token) {
                const metaTag = document.head.querySelector('meta[name="csrf-token"]');
                if (metaTag) {
                    metaTag.setAttribute('content', response.data.csrf_token);
                }
                window.axios.defaults.headers.common['X-CSRF-TOKEN'] = response.data.csrf_token;
            }
        } catch (error) {
            console.error('Erreur lors du rafraîchissement du token:', error);
        }
    };

    // Wrapper pour gérer les erreurs 419
    const enhancedOptions = {
        ...options,
        onError: async (errors: any) => {
            // Vérifier si c'est une erreur 419
            if (errors?.response?.status === 419 ||
                errors?.message?.includes('419') ||
                errors?.message?.includes('token') ||
                errors?.message?.includes('expired')) {

                try {
                    // Rafraîchir le token et réessayer
                    await refreshTokenIfNeeded();
                    originalSubmit(data, { ...options, onError: options.onError });
                    return;
                } catch (refreshError) {
                    console.error('Erreur lors du rafraîchissement du token:', refreshError);
                    window.location.reload();
                    return;
                }
            }

            // Autres erreurs
            options.onError?.(errors);
        }
    };

    // Rafraîchir le token si le formulaire est ouvert depuis plus de 5 minutes
    const formStartTime = Date.now();
    const timeSinceStart = Date.now() - formStartTime;

    if (timeSinceStart > 5 * 60 * 1000) {
        refreshTokenIfNeeded().then(() => {
            originalSubmit(data, enhancedOptions);
        });
    } else {
        originalSubmit(data, enhancedOptions);
    }
}

// Version sécurisée qui n'interfère pas avec useForm d'Inertia
export function addCsrfProtectionToRouterMethodsSafe() {
    // Ne pas modifier router.post/put/patch/delete car ils sont utilisés par useForm
    // Utiliser une approche plus conservatrice via les intercepteurs

    // Intercepteur global pour les erreurs 419
    let isRetrying = false;

    const originalVisit = router.visit;
    router.visit = function(url, options = {}) {
        const enhancedOptions = {
            ...options,
            onError: async (errors: any) => {
                if ((errors?.response?.status === 419 ||
                    errors?.message?.includes('419') ||
                    errors?.message?.includes('token')) && !isRetrying) {

                    isRetrying = true;
                    try {
                        // Rafraîchir le token
                        const response = await window.axios.get('/csrf-token');
                        if (response.data.csrf_token) {
                            const metaTag = document.head.querySelector('meta[name="csrf-token"]');
                            if (metaTag) {
                                metaTag.setAttribute('content', response.data.csrf_token);
                            }
                            window.axios.defaults.headers.common['X-CSRF-TOKEN'] = response.data.csrf_token;

                            // Réessayer la requête
                            setTimeout(() => {
                                isRetrying = false;
                                originalVisit.call(this, url, options);
                            }, 100);
                            return;
                        }
                    } catch (refreshError) {
                        console.error('Erreur lors du rafraîchissement du token:', refreshError);
                        isRetrying = false;
                        window.location.reload();
                        return;
                    }
                }

                // Autres erreurs
                if (options && typeof options.onError === 'function') {
                    options.onError(errors);
                }
                isRetrying = false;
            }
        };

        return originalVisit.call(this, url, enhancedOptions);
    };

    console.log('[CSRF Protection] Safe router protection enabled');
}

// Fonction simplifiée pour initialiser la protection CSRF
export function initializeCsrfProtection() {
    // Utiliser la version sécurisée
    addCsrfProtectionToRouterMethodsSafe();

    // Ajouter un écouteur pour les erreurs globales
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason?.response?.status === 419) {
            console.warn('[CSRF Protection] Unhandled 419 error detected, reloading page');
            // Attendre un peu avant de recharger pour éviter les boucles infinies
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }
    });

    // Rafraîchir le token périodiquement
    setInterval(async () => {
        try {
            const response = await window.axios.get('/csrf-token');
            if (response.data.csrf_token) {
                const metaTag = document.head.querySelector('meta[name="csrf-token"]');
                if (metaTag) {
                    metaTag.setAttribute('content', response.data.csrf_token);
                }
                window.axios.defaults.headers.common['X-CSRF-TOKEN'] = response.data.csrf_token;
                console.log('[CSRF Protection] Token refreshed automatically');
            }
        } catch (error) {
            console.error('[CSRF Protection] Error refreshing token:', error);
        }
    }, 10 * 60 * 1000); // Toutes les 10 minutes

    console.log('[CSRF Protection] Global CSRF protection initialized');
}

// Fonction pour configurer automatiquement la base URL selon l'environnement
export function configureBaseUrl() {
    // Détecter automatiquement la base URL
    const currentUrl = window.location.href;
    const pathname = window.location.pathname;

    // Si on est sur localhost/soft_edu/public, configurer axios avec cette base
    if (currentUrl.includes('/soft_edu/public')) {
        const baseUrl = window.location.protocol + '//' + window.location.host + '/soft_edu/public';
        window.axios.defaults.baseURL = baseUrl;
        console.log('[URL Config] Base URL set to:', baseUrl);
    } else if (currentUrl.includes('/soft_edu')) {
        const baseUrl = window.location.protocol + '//' + window.location.host + '/soft_edu';
        window.axios.defaults.baseURL = baseUrl;
        console.log('[URL Config] Base URL set to:', baseUrl);
    } else {
        // URL normale (ex: localhost:8000 ou domaine en production)
        const baseUrl = window.location.protocol + '//' + window.location.host;
        window.axios.defaults.baseURL = baseUrl;
        console.log('[URL Config] Base URL set to:', baseUrl);
    }

    // Configurer les headers par défaut
    window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    window.axios.defaults.headers.common['Accept'] = 'application/json';

    // Ajouter un intercepteur pour loguer les requêtes et détecter les problèmes d'URL
    window.axios.interceptors.request.use(
        (config) => {
            console.log('[Axios Request]', config.method?.toUpperCase(), config.url);
            return config;
        },
        (error) => {
            console.error('[Axios Request Error]', error);
            return Promise.reject(error);
        }
    );
}
